import type { Knowledge } from "api/api.types";
import { InfinityLoader } from "common/components";
import { useSelector } from "common/utilities/react-redux";
import { useTranslate } from "common/utilities/useTranslate";
import type { TrackPage } from "domains/analytics/types";
import { getTopicsFilterSelectedIds } from "domains/monitor/filter/selectors";
import { VideoListItem } from "domains/videos/components/VideoListItem";
import { useMedia } from "domains/viewport/useMedia";

type VideoListProps = {
  videos: Knowledge[];
  emptyMessage?: string;
  hasNextPage?: boolean;
  isPending?: boolean;
  lockTiles?: boolean;
  selectedId?: string;
  showEmptyMessage?: boolean;
  trackPage: TrackPage;
  loadNextPage?: () => void;
  getToLink: (id: string) => string;
};

const LATEST_KNOWLEDGE_PUBLIC_LIMIT = 4;

export const VideoList = ({
  videos,
  hasNextPage = false,
  isPending = false,
  selectedId,
  trackPage,
  lockTiles,
  emptyMessage,
  showEmptyMessage = true,
  loadNextPage,
  getToLink,
}: VideoListProps) => {
  const isList = useMedia({ small: true, medium: true }, false);
  const selectedTopics = useSelector(getTopicsFilterSelectedIds);
  const translate = useTranslate();

  if (showEmptyMessage && videos.length === 0 && !isPending) {
    if (emptyMessage) {
      return <p>{emptyMessage}</p>;
    }
    return (
      <p>{translate(selectedTopics.length === 0 ? "app.monitor.empty_message" : "app.monitor.empty_message_filter")}</p>
    );
  }

  return (
    <InfinityLoader isPending={isPending} hasNextPage={hasNextPage} loadNextPage={loadNextPage}>
      {videos.map((video, index) => (
        <VideoListItem
          title={video.title}
          description={video.summary}
          imageUrl={video.images?.x_small}
          source={video.source}
          timestamp={video.time}
          key={video.id}
          link={getToLink(video.id)}
          isSelected={!isList && selectedId === video.id}
          isLocked={lockTiles && index >= LATEST_KNOWLEDGE_PUBLIC_LIMIT}
          trackPage={trackPage}
          isFirstLocked={index === LATEST_KNOWLEDGE_PUBLIC_LIMIT}
        />
      ))}
    </InfinityLoader>
  );
};
